import { RouteManeuver } from 'common/helpers/direction.helper';
import { isRouteManeuver } from 'common/helpers/direction.helper';

export type RouteLocation = string | google.maps.LatLngLiteral;

export interface IPrintRouteStep {
  instruction: string;
  distanceText?: string | null;
  durationText?: string | null;
  maneuver?: RouteManeuver;
}

export interface IPrintRoute {
  steps: IPrintRouteStep[];
  distanceText?: string | null;
  durationText?: string | null;
  warnings: string[];
  addressFrom: string | null;
}

interface IGetRouteForPrintParams {
  origin: RouteLocation;
  destination: RouteLocation;
}

export async function getRouteForPrint({ origin, destination }: IGetRouteForPrintParams): Promise<IPrintRoute | null> {
  const { Route } = await google.maps.importLibrary('routes');

  const { routes } = await Route.computeRoutes({
    origin,
    destination,
    travelMode: 'DRIVING',
    fields: ['legs', 'localizedValues', 'warnings'],
  });

  const route = routes?.[0];

  if (!route) {
    return null;
  }

  const startLocation = await getAddressString(origin);
  return {
    addressFrom: startLocation,
    distanceText: route.localizedValues?.distance,
    durationText: route.localizedValues?.duration,
    warnings: [...(route.warnings ?? [])],

    steps: (route.legs ?? []).flatMap((leg) =>
      leg.steps.map((step) => {
        return {
          instruction: step.instructions ?? '',
          distanceText: step.localizedValues?.distance ?? `${step.distanceMeters} m`,
          durationText: step.localizedValues?.staticDuration,
          maneuver: isRouteManeuver(step.maneuver) ? step.maneuver : undefined,
        };
      }),
    ),
  };
}

const getAddressString = async (location: RouteLocation): Promise<string> => {
  if (typeof location === 'string') {
    return location;
  }

  try {
    const { Geocoder } = await google.maps.importLibrary('geocoding');

    const geocoder = new Geocoder();
    const { results } = await geocoder.geocode({
      location: {
        lat: location.lat,
        lng: location.lng,
      },
    });
    return results[0]?.formatted_address ?? '';
  } catch (error) {
    console.error('Failed to resolve address:', error);
    return '';
  }
};
